import {
  INTEGRATION_EVENT_DEFINITIONS,
  INTEGRATION_PING_EVENT,
  Permissions as P,
  type ClientSecretStatus,
  type IntegrationSyncStatus,
  type PermissionCode,
  type SyncItemKind,
  type SyncItemOutcome,
  type SyncSummaryDto,
  type WebhookDeliveryStatus,
} from '@fas/types';
import type { BadgeTone } from '@fas/ui';

/** Who may open the integrations area (Phase 3A). */
export const INTEGRATION_READERS: PermissionCode[] = [P.INTEGRATION_READ, P.INTEGRATION_MANAGE];

const EVENT_LABELS = new Map<string, string>([...INTEGRATION_EVENT_DEFINITIONS.map((d) => [d.type, d.label] as const), [INTEGRATION_PING_EVENT, 'Test event']]);

export function eventLabel(type: string): string {
  return EVENT_LABELS.get(type) ?? type;
}

export const DELIVERY_STATUS: Record<WebhookDeliveryStatus, { label: string; tone: BadgeTone }> = {
  PENDING: { label: 'Waiting', tone: 'warning' },
  SUCCEEDED: { label: 'Delivered', tone: 'success' },
  FAILED: { label: 'Gave up', tone: 'danger' },
};

export const SECRET_STATUS: Record<ClientSecretStatus, { label: string; tone: BadgeTone }> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
  REVOKED: { label: 'Revoked', tone: 'neutral' },
};

export const SYNC_STATUS: Record<IntegrationSyncStatus, { label: string; tone: BadgeTone }> = {
  QUEUED: { label: 'Queued', tone: 'neutral' },
  RUNNING: { label: 'Running', tone: 'info' },
  DRY_RUN: { label: 'Dry run', tone: 'info' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  FAILED: { label: 'Failed', tone: 'danger' },
};

export const SYNC_OUTCOME: Record<SyncItemOutcome, { label: string; tone: BadgeTone }> = {
  CREATED: { label: 'Created', tone: 'success' },
  UPDATED: { label: 'Updated', tone: 'info' },
  ADOPTED: { label: 'Linked', tone: 'info' },
  UNCHANGED: { label: 'Unchanged', tone: 'neutral' },
  DEACTIVATED: { label: 'Ended', tone: 'warning' },
  ERROR: { label: 'Error', tone: 'danger' },
};

/** The counts of a run, in the order they are shown. */
export const SYNC_COUNTS: Array<[keyof Omit<SyncSummaryDto, 'total'>, string]> = [
  ['created', 'created'],
  ['updated', 'updated'],
  ['adopted', 'linked'],
  ['deactivated', 'ended'],
  ['unchanged', 'unchanged'],
  ['errors', 'errors'],
];

/** "3 created · 1 updated · 2 errors" — the non-zero counts. */
export function summaryLine(summary: SyncSummaryDto | null): string {
  if (!summary) return '';
  return (
    SYNC_COUNTS.filter(([key]) => summary[key] > 0)
      .map(([key, label]) => `${summary[key]} ${key === 'errors' && summary[key] === 1 ? 'error' : label}`)
      .join(' · ') || 'Nothing to do'
  );
}

export const SYNC_KIND: Record<SyncItemKind, string> = {
  academicYear: 'Academic year',
  term: 'Term',
  course: 'Course',
  section: 'Section',
  load: 'Faculty load',
  schedule: 'Class meeting',
  faculty: 'Faculty member',
  calendarEvent: 'Calendar event',
  leave: 'Leave',
};

const FIELD_LABELS: Record<string, string> = {
  academicYearId: 'academic year',
  courseId: 'course',
  termId: 'term',
  departmentId: 'department',
  programId: 'program',
  sectionCode: 'section code',
  termType: 'term type',
  startDate: 'start date',
  endDate: 'end date',
  lectureUnits: 'lecture units',
  labUnits: 'lab units',
  lectureHoursPerWeek: 'lecture hours',
  labHoursPerWeek: 'lab hours',
  facultyId: 'faculty member',
  courseSectionId: 'section',
  facultyLoadId: 'load',
  dayOfWeek: 'day',
  startTime: 'start time',
  endTime: 'end time',
  locationId: 'room',
  scheduledModality: 'modality',
  loadType: 'load type',
  onlinePlatform: 'online platform',
  meetingUrl: 'meeting link',
  effectiveFrom: 'first date',
  effectiveTo: 'last date',
  contactHoursPerWeek: 'contact hours',
  firstName: 'first name',
  middleName: 'middle name',
  lastName: 'last name',
  institutionalEmail: 'e-mail',
  mobileNumber: 'mobile number',
  employmentTypeCode: 'employment type',
  employmentStatus: 'employment status',
  collegeId: 'college',
  positionTitle: 'position',
  academicRank: 'rank',
  hireDate: 'hire date',
  shiftModel: 'shift',
  regularWorkStart: 'work start',
  regularWorkEnd: 'work end',
  breakStart: 'break start',
  breakEnd: 'break end',
  workDays: 'work days',
  dailyRequiredMinutes: 'daily minutes',
  weeklyRequiredMinutes: 'weekly minutes',
  eventType: 'event type',
  campusId: 'campus',
  suspendsClasses: 'suspends classes',
  suspendsWork: 'suspends work',
  leaveType: 'leave type',
};

/** A changed field of a synced record, in words. */
export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/** Secret lifetimes offered when a secret is created; null never expires. */
export const SECRET_LIFETIMES: Array<{ value: string; label: string; days: number | null }> = [
  { value: '90', label: '90 days', days: 90 },
  { value: '365', label: '1 year', days: 365 },
  { value: '730', label: '2 years', days: 730 },
  { value: 'never', label: 'Does not expire', days: null },
];

/** "10.0.0.0/8, 203.0.113.7" or one per line → list; the server validates each entry. */
export function parseAddressList(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}
